/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Find someone by name, then call them.
 *
 * The console could only ever dial a typed number, which assumes the agent
 * already has it. In practice they have a name -- the customer who called
 * yesterday, the account a manager just asked them to chase -- and looking it
 * up meant opening the CRM in another tab, copying a number out and pasting it
 * back. A number retyped from another tab is also the number that arrives in a
 * slightly different format and stops matching on the next inbound call.
 *
 * Dialling from here passes the record context through, so the call lands on
 * that record's timeline at wrap-up without anyone having to attach it.
 */

import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { CircularProgress, IconButton, TextField, Tooltip } from "@mui/material";
import {
  Business,
  Call,
  ConfirmationNumber,
  LocalOffer,
  Person,
  PersonSearch,
  Search,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import colors from "styles/tokens/colors";
import { useAuth } from "auth/Auth";

const cc = colors.callCenter;

const TYPE_META = {
  contact: { label: "Contact", icon: <Person fontSize="small" />, table: "contacts" },
  lead: { label: "Lead", icon: <PersonSearch fontSize="small" />, table: "leads" },
  account: { label: "Account", icon: <Business fontSize="small" />, table: "accounts" },
  deal: { label: "Deal", icon: <LocalOffer fontSize="small" />, table: "deals" },
  case: { label: "Case", icon: <ConfirmationNumber fontSize="small" />, table: "cases" },
};

const metaFor = (type) => TYPE_META[type] || TYPE_META.contact;

/** Mobile first: it is the number a person actually answers. */
const numberFor = (match) => (match?.mobile || match?.phone || "").trim();

export default function RecordDialSearch({ onDial, canDial, disabledReason }) {
  const { axiosInstance } = useAuth();
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Debounced so a four-letter name is one query, not four. Same shape as
  // LinkRecordPanel's search, against the same endpoint.
  useEffect(() => {
    if (term.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const id = setTimeout(() => {
      axiosInstance
        .get("/call-console/search", { params: { q: term.trim() } })
        .then((r) => {
          if (!cancelled) setResults(Array.isArray(r.data) ? r.data : []);
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [term, axiosInstance]);

  const dial = useCallback(
    (match) => {
      const to = numberFor(match);
      if (!to) return;
      onDial({
        to,
        context: {
          parent_table: metaFor(match.type).table,
          parent_id: match.id,
          account_id: match.type === "account" ? match.id : match.account_id || null,
          contact_id: match.type === "contact" ? match.id : match.contact_id || null,
          lead_id: match.type === "lead" ? match.id : null,
          deal_id: match.type === "deal" ? match.id : null,
          case_id: match.type === "case" ? match.id : null,
        },
      });
    },
    [onDial]
  );

  return (
    <MDBox display="flex" flexDirection="column" gap={1}>
      <TextField
        size="small"
        fullWidth
        label="Or search for someone"
        placeholder="Name, company or email"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        InputProps={{
          startAdornment: <Search sx={{ fontSize: 18, color: cc.muted, mr: 1 }} />,
        }}
      />

      {searching ? (
        <MDBox display="flex" justifyContent="center" py={1}>
          <CircularProgress size={18} />
        </MDBox>
      ) : null}

      {!searching && term.trim().length >= 2 && results.length === 0 ? (
        <MDTypography variant="caption" color="text">
          Nothing matched that name.
        </MDTypography>
      ) : null}

      {results.map((m) => {
        const to = numberFor(m);
        const meta = metaFor(m.type);
        // A record with no number on it is still worth showing -- it tells the
        // agent the record exists -- but saying so beats a button that silently
        // does nothing when pressed.
        const tooltip = !to
          ? `${meta.label} has no phone number on record`
          : !canDial
          ? disabledReason || "You cannot place a call right now"
          : `Call ${to}`;

        return (
          <MDBox
            key={`${m.type}-${m.id}`}
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              p: 1,
              borderRadius: "8px",
              border: `1px solid ${cc.cardBorder}`,
              "&:hover": { bgcolor: cc.surface },
            }}
          >
            <MDBox sx={{ color: cc.muted, display: "flex" }}>{meta.icon}</MDBox>
            <MDBox flexGrow={1} minWidth={0}>
              <MDTypography variant="button" fontWeight="medium" color="dark" display="block">
                {m.name}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                {[meta.label, to || m.subtitle].filter(Boolean).join(" · ")}
              </MDTypography>
            </MDBox>
            <Tooltip title={tooltip}>
              <span>
                <IconButton
                  size="small"
                  color="info"
                  disabled={!to || !canDial}
                  onClick={() => dial(m)}
                >
                  <Call fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </MDBox>
        );
      })}
    </MDBox>
  );
}

RecordDialSearch.propTypes = {
  onDial: PropTypes.func.isRequired,
  canDial: PropTypes.bool,
  disabledReason: PropTypes.string,
};

RecordDialSearch.defaultProps = {
  canDial: true,
  disabledReason: null,
};
